import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import {
  type Capacidades,
  type ConversaAtiva,
  type ConversaCompleta as Conversa,
  type DetalheDaResposta,
  type ItemAtendimento,
  type ItemDaConversa,
  type ItemMensagem,
  type ItemSistema,
  api,
  explicarMotivo,
} from '../lib/api.js';
import { EstadoNoDetalhe } from './EstadoDoBot.js';
import { NomeDoAluno } from './NomeDoAluno.js';
import { CorpoWhatsapp } from './PreviaWhatsapp.js';

/**
 * A conversa como diálogo, por pessoa.
 *
 * O detalhe do atendimento continua sendo a tela de auditoria — nó da árvore,
 * faixa, via, cosseno em cada passo. Serve para julgar uma decisão do bot e é
 * péssima para LER. Esta tela é a outra leitura: quem vai atender abre e lê o
 * que foi dito, no formato em que a pessoa do outro lado leu.
 *
 * TRÊS COISAS QUE ELA NÃO FAZ, DE PROPÓSITO:
 *
 * 1. Não tem campo de resposta. O painel não envia mensagem: quem responde é a
 *    secretaria pelo WhatsApp. Uma caixa de texto aqui criaria a expectativa
 *    errada, e a mensagem que ninguém enviou é pior que a tela sem caixa.
 *
 * 2. Não esconde a diferença entre o que SAIU e o que seria reconstruído. Em
 *    sombra nada chegou ao aluno; em atendimento antigo o literal não foi
 *    gravado. Nos dois casos a bolha diz isso.
 *
 * 3. Não mostra score por padrão. Quem lê a conversa não quer ver cosseno;
 *    quem investiga abre o detalhe da bolha.
 *
 * O texto do bot passa pelo MESMO renderizador de nós da prévia da FAQ
 * (`CorpoWhatsapp`): nada aqui chega perto de `innerHTML`.
 */

const CLASSE_SISTEMA: Record<ItemSistema['classe'], { icone: string; cor: string }> = {
  transbordo: { icone: '🎯', cor: 'blue' },
  pausa: { icone: '👤', cor: 'orange' },
  retomada: { icone: '↩', cor: 'teal' },
  sessao: { icone: '⌫', cor: 'gray' },
  bloqueio: { icone: '🚫', cor: 'orange' },
};

const ROTULO_FAIXA: Record<string, string> = {
  direto: 'faixa alta — respondeu sem LLM',
  llm: 'faixa média — classificador escolheu',
  lacuna: 'abaixo do limite — virou lacuna',
};

const ROTULO_MOTIVO: Record<string, string> = {
  sem_candidatos: 'a busca não trouxe candidato',
  score_baixo: 'cosseno abaixo do limite inferior',
  llm_nenhuma: 'o classificador respondeu NENHUMA',
  ma_resposta: 'o aluno marcou 👎',
  baixa_confianca: 'baixa confiança',
  solicitado: 'o aluno pediu atendente',
  humano_assumiu: 'um humano assumiu a conversa',
};

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function diaCurto(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** "hoje", "ontem" ou a data por extenso. É o separador entre dias. */
function rotuloDoDia(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();
  if (mesmoDia(d, hoje)) return 'hoje';
  const ontem = new Date(hoje.getTime() - 24 * 60 * 60 * 1_000);
  if (mesmoDia(d, ontem)) return 'ontem';
  return d.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: d.getFullYear() === hoje.getFullYear() ? undefined : 'numeric',
  });
}

/** Dia local do item, para saber onde entra o separador de data. */
function chaveDoDia(iso: string): string {
  return new Date(iso).toDateString();
}

export function ConversaCompleta({
  atendimentoId,
  capacidades,
  aoFechar,
  rotuloFechar = 'Fechar',
  ancora,
  aoVerDetalheTecnico,
  altura = '60vh',
}: {
  atendimentoId: string;
  capacidades: Capacidades;
  /** Sem isto, nenhum botão de sair: na tela de duas colunas não há o que fechar. */
  aoFechar?: () => void;
  rotuloFechar?: string;
  /**
   * Atendimento a destacar e rolar até ele, em vez de abrir no fim.
   *
   * É o fluxo do protocolo: a secretaria busca AP-001009 e quer cair NAQUELE
   * ponto do fio, não na última mensagem.
   */
  ancora?: string;
  /**
   * Abre a visão de auditoria do atendimento (passo a passo com nó, faixa,
   * via, cosseno). O link vive na emenda de cada atendimento porque o detalhe
   * é sobre AQUELE atendimento, que a pessoa está lendo.
   */
  aoVerDetalheTecnico?: (atendimentoId: string) => void;
  /**
   * Altura do painel de rolagem do fio.
   *
   * Quem monta a tela sabe quanto espaço há; o fio não. O padrão de 60vh
   * servia para o fio sobreposto, e numa tela de duas colunas desperdiça
   * metade do monitor — ver a nota da rolagem abaixo, que é onde isso doeu.
   */
  altura?: string;
}): JSX.Element {
  const [conversa, setConversa] = useState<Conversa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Páginas mais antigas, já na ordem de leitura (mais antiga primeiro). */
  const [anteriores, setAnteriores] = useState<ItemDaConversa[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [temMais, setTemMais] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);

  /** Estado ATUAL da sessão, para as ações do topo. */
  const [estado, setEstado] = useState<ConversaAtiva | null>(null);
  const [erroRedis, setErroRedis] = useState(false);

  const fio = useRef<HTMLDivElement>(null);
  /** A rolagem vai ao fim UMA vez: depois disso a pessoa está no controle. */
  const jaRolou = useRef(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<Conversa>(
        `/admin/conversas/${atendimentoId}/historico${
          ancora ? `?ancora=${encodeURIComponent(ancora)}` : ''
        }`,
      );
      setConversa(r);
      setCursor(r.cursor);
      setTemMais(r.temMaisAntigos);
      setErro(null);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao ler a conversa'));
    }
  }, [atendimentoId, ancora]);

  const lerEstado = useCallback(async () => {
    try {
      const r = await api.get<{ estado: ConversaAtiva | null }>(
        `/admin/conversas/estado?atendimento=${atendimentoId}`,
      );
      setEstado(r.estado);
      setErroRedis(false);
    } catch {
      setErroRedis(true);
    }
  }, [atendimentoId]);

  useEffect(() => {
    void carregar();
    void lerEstado();
  }, [carregar, lerEstado]);

  /**
   * Onde o fio abre.
   *
   * Com âncora, NELA: quem buscou por protocolo quer aquele ponto do fio.
   *
   * Sem âncora, na ÚLTIMA PERGUNTA DO ALUNO — e não no fim absoluto. Abrir no
   * fim parece o certo (é o que um aplicativo de mensagem faz) e foi o bug
   * relatado: a última rodada do bot são três bolhas (saudação + resposta +
   * menu), que medem ~480px juntas, e o painel tinha 60vh — 400px num notebook.
   * O resultado era uma tela inteira de bolhas verdes alinhadas à direita, com
   * a pergunta que as gerou, a emenda do atendimento e o separador de data
   * todos acima da área visível. Quem abriu viu "o lado esquerdo vazio".
   *
   * Ancorar na última pergunta garante o par pergunta→resposta na abertura. O
   * `Math.min` preserva o comportamento de aplicativo de mensagem quando a
   * última rodada é curta: aí o alvo fica depois do fim possível e a rolagem
   * para no fim, como antes.
   */
  useEffect(() => {
    if (!conversa || jaRolou.current) return;
    const caixa = fio.current;
    if (!caixa) return;

    const alvo = ancora
      ? caixa.querySelector(`[data-atendimento="${ancora}"]`)
      : // `querySelectorAll` + último: é a pergunta que abriu a última rodada.
        [...caixa.querySelectorAll('[data-autor="aluno"]')].at(-1);

    const maximo = caixa.scrollHeight - caixa.clientHeight;
    if (alvo instanceof HTMLElement) {
      // `scrollTop` calculado à mão em vez de `scrollIntoView`: este último
      // rola a PÁGINA junto, levando o cabeçalho e as ações embora.
      const topo = alvo.offsetTop - caixa.offsetTop - 12;
      caixa.scrollTop = Math.max(0, Math.min(topo, maximo));
    } else {
      caixa.scrollTop = caixa.scrollHeight;
    }
    jaRolou.current = true;
  }, [conversa, ancora]);

  // Âncora nova (outro protocolo buscado) rola de novo.
  useEffect(() => {
    jaRolou.current = false;
  }, [atendimentoId, ancora]);

  async function carregarAnteriores(): Promise<void> {
    if (!cursor) return;
    setCarregandoMais(true);
    try {
      const r = await api.get<Conversa>(
        `/admin/conversas/${atendimentoId}/historico?antes=${encodeURIComponent(cursor)}`,
      );
      // Páginas antigas entram ANTES das que já estão na tela, e a posição da
      // rolagem fica onde está: puxar a leitura para o topo a cada clique
      // perderia o lugar de quem estava lendo.
      setAnteriores((atuais) => [...r.itens, ...atuais]);
      setCursor(r.cursor);
      setTemMais(r.temMaisAntigos);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao carregar os atendimentos anteriores'));
    } finally {
      setCarregandoMais(false);
    }
  }

  if (erro && !conversa) {
    return (
      <Card withBorder padding="md">
        <Stack gap="sm" align="flex-start">
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
          <Group>
            <Button onClick={() => void carregar()}>Tentar de novo</Button>
            {aoFechar && (
              <Button variant="subtle" onClick={aoFechar}>
                {rotuloFechar}
              </Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  }

  if (!conversa) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const itens = [...anteriores, ...conversa.itens];
  const carregados = conversa.atendimentosCarregados + contarAtendimentos(anteriores);

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="wrap">
            <Title order={2} size="h4">
              Conversa
            </Title>
            <NomeDoAluno nome={conversa.nome} origem={conversa.nomeOrigem} />
            <Text size="sm" c="dimmed" ff="monospace">
              {conversa.telefone ?? '—'}
            </Text>
            {!conversa.telefoneCompleto && (
              <Text size="xs" c="dimmed">
                mascarado para o seu papel
              </Text>
            )}
          </Group>
          {aoFechar && <Button onClick={aoFechar}>{rotuloFechar}</Button>}
        </Group>

        <Text size="xs" c="dimmed">
          {carregados} de {conversa.totalAtendimentos} atendimento(s) deste telefone
          {conversa.tipoContato ? ` · ${conversa.tipoContato}` : ''}
          {conversa.perfil ? ` · ${conversa.perfil}` : ''}
        </Text>

        {/* As mesmas ações da aba Conversas, no mesmo componente: quem percebe
            o bot calado destrava aqui, sem ir procurar outra tela. */}
        <EstadoNoDetalhe
          conversa={estado}
          capacidades={capacidades}
          erroRedis={erroRedis}
          aoMudar={() => void lerEstado()}
        />

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Divider />

        <Box
          ref={fio}
          /* Rolagem própria: o fio pode ter centenas de bolhas, e a página
             inteira rolando levaria o cabeçalho e as ações embora. */
          style={{ height: altura, overflowY: 'auto' }}
          px="xs"
        >
          <Stack gap={2}>
            {temMais && (
              <Group justify="center" my="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  loading={carregandoMais}
                  onClick={() => void carregarAnteriores()}
                >
                  Carregar atendimentos mais antigos
                </Button>
              </Group>
            )}
            {!temMais && conversa.totalAtendimentos > 0 && (
              <Text size="xs" c="dimmed" ta="center" my="xs">
                Começo da conversa.
              </Text>
            )}

            {itens.map((item, i) => (
              <Linha
                key={item.id}
                item={item}
                diaAnterior={i > 0 ? chaveDoDia(itens[i - 1]!.em) : null}
                ancora={ancora}
                {...(aoVerDetalheTecnico ? { aoVerDetalheTecnico } : {})}
              />
            ))}

            {itens.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" my="md">
                Nenhuma mensagem registrada para este telefone.
              </Text>
            )}
          </Stack>
        </Box>

        {/* Dito com palavras porque a ausência de caixa de texto não explica a
            si mesma — e alguém vai procurar onde responder. */}
        <Text size="xs" c="dimmed">
          O painel não envia mensagem. Quem responde ao aluno é a secretaria, pelo WhatsApp.
        </Text>
      </Stack>
    </Card>
  );
}

function contarAtendimentos(itens: readonly ItemDaConversa[]): number {
  return itens.filter((i) => i.tipo === 'atendimento').length;
}

// ---------------------------------------------------------------------------
// Uma linha do fio
// ---------------------------------------------------------------------------

function Linha({
  item,
  diaAnterior,
  ancora,
  aoVerDetalheTecnico,
}: {
  item: ItemDaConversa;
  diaAnterior: string | null;
  ancora?: string;
  aoVerDetalheTecnico?: (atendimentoId: string) => void;
}): JSX.Element {
  const mudouODia = diaAnterior !== null && diaAnterior !== chaveDoDia(item.em);

  return (
    <>
      {(mudouODia || diaAnterior === null) && (
        <Divider
          my="xs"
          label={
            <Text size="xs" c="dimmed" tt="lowercase">
              {rotuloDoDia(item.em)}
            </Text>
          }
          labelPosition="center"
        />
      )}
      {item.tipo === 'atendimento' ? (
        <SeparadorDeAtendimento
          item={item}
          destacado={ancora === item.atendimentoId}
          {...(aoVerDetalheTecnico ? { aoVerDetalheTecnico } : {})}
        />
      ) : item.tipo === 'sistema' ? (
        <LinhaDeSistema item={item} />
      ) : (
        <Bolha item={item} />
      )}
    </>
  );
}

/**
 * A emenda entre sessões.
 *
 * Discreto de propósito: é a mesma conversa e a mesma pessoa: o atendimento
 * só mudou porque a sessão expirou com 30 min de silêncio. Uma quebra de tela
 * aqui contaria duas histórias onde há uma.
 */
function SeparadorDeAtendimento({
  item,
  destacado,
  aoVerDetalheTecnico,
}: {
  item: ItemAtendimento;
  destacado?: boolean;
  aoVerDetalheTecnico?: (atendimentoId: string) => void;
}): JSX.Element {
  return (
    // `data-atendimento` é o alvo da rolagem por âncora.
    <Box data-atendimento={item.atendimentoId} py={destacado ? 4 : 0}>
      <Divider
        my="sm"
        variant="dashed"
        labelPosition="center"
        color={destacado ? 'var(--mantine-primary-color-filled)' : undefined}
        label={
          /* `wrap` e não `nowrap`: com o link do detalhe técnico o rótulo
             passa de 60 caracteres, e a coluna da conversa é estreita. */
          <Group gap={6} wrap="wrap" justify="center">
            <Text size="xs" c="dimmed" fw={destacado ? 700 : 400}>
              {item.protocolo} · {diaCurto(item.em)}, {hora(item.em)}
            </Text>
            {/* O destaque tem TEXTO, não só a borda colorida. */}
            {destacado && (
              <Badge size="xs" variant="light">
                protocolo buscado
              </Badge>
            )}
            {item.modo === 'shadow' && (
              <Badge color="orange" size="xs" variant="light">
                sombra — nada enviado
              </Badge>
            )}
            {aoVerDetalheTecnico && (
              <Anchor
                component="button"
                type="button"
                size="xs"
                c="dimmed"
                onClick={() => aoVerDetalheTecnico(item.atendimentoId)}
              >
                ver detalhe técnico deste atendimento
              </Anchor>
            )}
          </Group>
        }
      />
    </Box>
  );
}

/** Mensagem de sistema: centralizada e discreta, como no WhatsApp. */
function LinhaDeSistema({ item }: { item: ItemSistema }): JSX.Element {
  const { icone, cor } = CLASSE_SISTEMA[item.classe];
  return (
    <Group justify="center" my={4}>
      <Paper
        bg={`var(--mantine-color-${cor}-light)`}
        px="sm"
        py={2}
        radius="sm"
        maw="90%"
      >
        {/* Ícone E texto: a cor do fundo é reforço, nunca o sinal. */}
        <Text size="xs" c="dimmed" ta="center">
          {icone} {item.texto} · {hora(item.em)}
        </Text>
      </Paper>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Bolhas
// ---------------------------------------------------------------------------

function Bolha({ item }: { item: ItemMensagem }): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const doBot = item.autor === 'bot';

  /**
   * Lado: o bot à direita, o aluno à esquerda.
   *
   * É o que a secretaria vê no WhatsApp dela quando abre essa conversa — as
   * mensagens do aeroclube saem do lado de quem envia. A tela de auditoria
   * usa o arranjo oposto porque ali o eixo é a decisão, não o diálogo.
   */
  const corpo = (
    <Stack gap={2} align={doBot ? 'flex-end' : 'flex-start'} maw="min(78%, 460px)">
      <Paper
        className={doBot && item.texto !== null && item.enviado ? 'previa-bolha' : undefined}
        withBorder={!doBot || item.texto === null || !item.enviado}
        bg={doBot && !item.enviado ? 'var(--mantine-color-orange-light)' : undefined}
        p="sm"
        radius="md"
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          ...(item.texto === null ? { borderStyle: 'dashed' } : {}),
        }}
        w="100%"
      >
        {item.texto === null ? (
          <Text size="sm" c="dimmed" fs="italic">
            (texto não gravado)
          </Text>
        ) : doBot ? (
          <CorpoWhatsapp texto={item.texto} />
        ) : (
          <Text size="sm">{item.texto}</Text>
        )}
      </Paper>

      <Group gap={6} wrap="nowrap">
        <Text size="xs" c="dimmed">
          {hora(item.em)}
        </Text>

        {/* Os três rótulos que mudam o que a bolha SIGNIFICA. Texto, não cor. */}
        {doBot && !item.enviado && (
          <Text size="xs" c="orange">
            não enviado (sombra)
          </Text>
        )}
        {doBot && item.textoAtualDaFaq && (
          <Text size="xs" c="dimmed">
            texto atual da FAQ, não o da época
          </Text>
        )}
        {doBot && item.texto === null && (
          <Text size="xs" c="dimmed">
            atendimento anterior ao registro de envio
          </Text>
        )}

        {item.detalhe && (
          <UnstyledButton onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
            <Text size="xs" c="dimmed" td="underline">
              {aberto ? 'esconder detalhe' : 'detalhe técnico'}
            </Text>
          </UnstyledButton>
        )}
      </Group>

      {/*
        `keepMounted={false}`: recolhido tem de significar AUSENTE, não
        escondido por CSS. Com o padrão, o cosseno continua no DOM — e para
        quem lê por leitor de tela a bolha não estaria recolhida de verdade.
      */}
      {item.detalhe && (
        <Collapse expanded={aberto} keepMounted={false}>
          <DetalheTecnico detalhe={item.detalhe} />
        </Collapse>
      )}
    </Stack>
  );

  return (
    // `data-autor` é o que a rolagem procura para abrir na última pergunta.
    <Group
      data-autor={item.autor}
      justify={doBot ? 'flex-end' : 'flex-start'}
      gap={0}
      my={2}
      wrap="nowrap"
    >
      {corpo}
    </Group>
  );
}

/** Recolhido por padrão: é a pergunta de quem investiga, não de quem lê. */
function DetalheTecnico({ detalhe }: { detalhe: DetalheDaResposta }): JSX.Element {
  const linhas: string[] = [];
  if (detalhe.faqPergunta) {
    linhas.push(
      `FAQ: ${detalhe.faqPergunta}${detalhe.faqCategoria ? ` (${detalhe.faqCategoria})` : ''}`,
    );
  }
  if (detalhe.faixa) linhas.push(ROTULO_FAIXA[detalhe.faixa] ?? detalhe.faixa);
  if (detalhe.via) linhas.push(`via ${detalhe.via}`);
  if (detalhe.cossenoDoTopo !== null) {
    linhas.push(`cosseno do topo ${detalhe.cossenoDoTopo.toFixed(4)}`);
  }
  if (detalhe.motivo) linhas.push(ROTULO_MOTIVO[detalhe.motivo] ?? detalhe.motivo);

  return (
    <Paper withBorder p="xs" radius="sm" mt={2}>
      <Stack gap={2}>
        {linhas.map((linha) => (
          <Text key={linha} size="xs" c="dimmed">
            {linha}
          </Text>
        ))}
        {detalhe.feedbackDoAluno && (
          <Badge
            size="xs"
            variant="light"
            color={detalhe.feedbackDoAluno === 'positivo' ? 'teal' : 'red'}
          >
            o aluno marcou {detalhe.feedbackDoAluno === 'positivo' ? '👍' : '👎'}
          </Badge>
        )}
        {detalhe.vereditoDaSecretaria && (
          <Badge
            size="xs"
            variant="light"
            color={detalhe.vereditoDaSecretaria === 'positivo' ? 'teal' : 'red'}
          >
            veredito da secretaria: {detalhe.vereditoDaSecretaria}
          </Badge>
        )}
        {linhas.length === 0 && !detalhe.feedbackDoAluno && !detalhe.vereditoDaSecretaria && (
          <Text size="xs" c="dimmed">
            Sem detalhe registrado nesta rodada.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
